/**
 * PhaseCasualtyControls.jsx v1.0.0 - 2026-10-13 (Tab 2 plan, step 3b)
 *
 * The casualty profile of one phase, next to its posture: enemy resistance, enemy strength ratio of this phase, maneuver
 * group (attack forms only), special condition, and the foothold / link-up day. Every field reaches the casualty rate through
 * constants/phaseCasualtyTable.js (nothing here is decoration). Empty = inherit (resistance: derived from the enemy assessment).
 *
 * Props: phase = { operational_posture, resistance, enemy_strength_ratio, maneuver_group, special_condition, foothold_day },
 *        onChange(field, value), disabled.
 */
import React, { useContext, createContext } from 'react';
import { useTranslation } from 'react-i18next';
import HelpText from './HelpText';
import { Box, Chip, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { WEATHER_CONDITIONS } from '../constants/operationsConfig';
import { phaseAdvanceKm, ADVANCE_KM_PER_DAY } from '../calculationEngine/modules/advanceModel';
import {
  PHASE_FORMS, POSTURE_TO_FORM, MANEUVER_GROUPS, SPECIAL_CONDITIONS, DEFAULT_FOOTHOLD_DAY, RESISTANCE_ANCHORS
} from '../constants/phaseCasualtyTable';

/** Set by Tab 2: the enemy combat power index and the level it suggests (null when not provided). */
export const EnemyIndexContext = createContext(null);
/** Set by Tab 2: the operation weather key (the default of a phase with no weather of its own). */
export const OperationWeatherContext = createContext(null);

const statusColor = (status) => (status === 'SOURCED' ? 'success' : status === 'DERIVED' ? 'info' : 'warning');

const PhaseCasualtyControls = ({ phase, onChange, disabled = false }) => {
  const { t } = useTranslation();
  const enemy = useContext(EnemyIndexContext);
  const operationWeather = useContext(OperationWeatherContext);
  const posture = phase?.operational_posture;
  const mapped = POSTURE_TO_FORM[posture];
  const isAttack = mapped ? PHASE_FORMS[mapped.form].family === 'ATTACK' : false;
  const isMarch = !mapped && Boolean(posture);   // march postures run through the separate march rate
  const condition = phase?.special_condition || '';
  const needsFoothold = condition === 'RIVER_ASSAULT' || condition === 'AIRBORNE';
  const set = (field, value) => onChange(field, value === '' ? null : value);

  // 2026-10-20 (R2-18, W2): weather of this phase; empty = the operation weather (Tab 2 Environmental Factors). Reaches roads, helicopters,
  // drones, the evacuation speed and the evacuation capacity of the days of the phase.
  const weatherControl = (
    <FormControl size="small" sx={{ minWidth: 200 }} disabled={disabled}>
      <InputLabel shrink>{t('tab2.phase_profile.weather')}</InputLabel>
      <Select
        displayEmpty
        notched
        value={phase?.weather ?? ''}
        label={t('tab2.phase_profile.weather')}
        onChange={(e) => set('weather', e.target.value)}
      >
        <MenuItem value=""><em>{t('tab2.phase_profile.weather_default')}{operationWeather && WEATHER_CONDITIONS[operationWeather] ? ` → ${WEATHER_CONDITIONS[operationWeather].label}` : ''}</em></MenuItem>
        {Object.keys(WEATHER_CONDITIONS).map((key) => (
          <MenuItem key={key} value={key}>{WEATHER_CONDITIONS[key].label}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  // 2026-10-21 (R2-30): advance of the brigade in this phase, km per day; empty = the default of the form, the resistance, the group and the condition
  const advanceForm = mapped ? mapped.form : null;
  const advanceLevel = phase?.resistance || (enemy ? enemy.level : null);
  let advanceDefault = null;
  if (advanceForm && ADVANCE_KM_PER_DAY[advanceForm] && advanceLevel) {
    try {
      advanceDefault = phaseAdvanceKm({ form: advanceForm, resistanceLevel: advanceLevel, group: phase?.maneuver_group || undefined, condition: phase?.special_condition || undefined, dayInPhase: 99, footholdDay: phase?.foothold_day });
    } catch (e) { advanceDefault = null; }
  }
  const advanceControl = (advanceForm && ADVANCE_KM_PER_DAY[advanceForm]) ? (
    <TextField
      size="small"
      type="number"
      label={t('tab2.phase_profile.advance')}
      value={phase?.advance_km_day ?? ''}
      onChange={(e) => set('advance_km_day', e.target.value === '' ? '' : Number(e.target.value))}
      inputProps={{ min: 0, step: 1 }}
      placeholder={advanceDefault === null ? t('tab2.phase_profile.advance_placeholder') : `${advanceDefault}`}
      InputLabelProps={{ shrink: true }}
      sx={{ width: 190 }}
      disabled={disabled}
    />
  ) : null;

  if (isMarch) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary">{t('tab2.phase_profile.march_note')}</Typography>
        {weatherControl}
      </Stack>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 88, fontWeight: 500 }}>
          {t('tab2.phase_profile.title')}
        </Typography>

        <FormControl size="small" sx={{ minWidth: 170 }} disabled={disabled}>
          <InputLabel>{t('tab2.phase_profile.resistance')}</InputLabel>
          <Select
            value={phase?.resistance ?? ''}
            label={t('tab2.phase_profile.resistance')}
            onChange={(e) => set('resistance', e.target.value)}
          >
            <MenuItem value=""><em>{t('tab2.phase_profile.resistance_auto')}{enemy ? ` → ${t(`tab2.phase_profile.${enemy.level.toLowerCase()}`)} ${enemy.index.toFixed(2)}` : ''}</em></MenuItem>
            <MenuItem value="LIGHT">{t('tab2.phase_profile.light')} ({RESISTANCE_ANCHORS.light})</MenuItem>
            <MenuItem value="MODERATE">{t('tab2.phase_profile.moderate')} ({RESISTANCE_ANCHORS.moderate})</MenuItem>
            <MenuItem value="HEAVY">{t('tab2.phase_profile.heavy')} ({RESISTANCE_ANCHORS.heavy})</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          type="number"
          label={t('tab2.phase_profile.enemy_ratio')}
          value={phase?.enemy_strength_ratio ?? ''}
          onChange={(e) => set('enemy_strength_ratio', e.target.value === '' ? '' : Number(e.target.value))}
          inputProps={{ min: 0.1, step: 0.1 }}
          placeholder={t('tab2.phase_profile.enemy_ratio_placeholder')}
          sx={{ width: 190 }}
          disabled={disabled}
        />

        {isAttack && (
          <FormControl size="small" sx={{ minWidth: 220 }} disabled={disabled}>
            <InputLabel>{t('tab2.phase_profile.group')}</InputLabel>
            <Select
              value={phase?.maneuver_group ?? ''}
              label={t('tab2.phase_profile.group')}
              onChange={(e) => set('maneuver_group', e.target.value)}
            >
              <MenuItem value=""><em>{t('tab2.phase_profile.group_default')}</em></MenuItem>
              {Object.entries(MANEUVER_GROUPS).map(([key, g]) => (
                <MenuItem key={key} value={key}>{g.label} (x{g.factor.toFixed(2)})</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl size="small" sx={{ minWidth: 230 }} disabled={disabled}>
          <InputLabel>{t('tab2.phase_profile.condition')}</InputLabel>
          <Select
            value={condition}
            label={t('tab2.phase_profile.condition')}
            onChange={(e) => set('special_condition', e.target.value)}
          >
            <MenuItem value=""><em>{t('tab2.phase_profile.condition_default')}</em></MenuItem>
            {Object.entries(SPECIAL_CONDITIONS).filter(([key]) => key !== 'NONE').map(([key, c]) => (
              <MenuItem key={key} value={key}>{c.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {needsFoothold && (
          <TextField
            size="small"
            type="number"
            label={t('tab2.phase_profile.foothold_day')}
            value={phase?.foothold_day ?? ''}
            onChange={(e) => set('foothold_day', e.target.value === '' ? '' : Number(e.target.value))}
            inputProps={{ min: 1, step: 1 }}
            placeholder={String(DEFAULT_FOOTHOLD_DAY[condition])}
            sx={{ width: 170 }}
            disabled={disabled}
          />
        )}

        {advanceControl}

        {weatherControl}

        {condition && SPECIAL_CONDITIONS[condition] && (
          <Chip size="small" variant="outlined" color={statusColor(SPECIAL_CONDITIONS[condition].status)}
            label={SPECIAL_CONDITIONS[condition].status} />
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block">{t('tab2.phase_profile.weather_help')}</Typography>
      {advanceControl && <Typography variant="caption" color="text.secondary" display="block">{t('tab2.phase_profile.advance_help')}</Typography>}
      <HelpText id="phase_resistance" />
    </Box>
  );
};

export default PhaseCasualtyControls;
